// Game-of-24 puzzle pool — the human-difficulty set.
//
// Source: HUMAN_PUZZLES in puzzles.data.ts (the Tree-of-Thoughts mirror of 4nums.com's
// rankings — 1362 solvable puzzles, numbers 1–13, ranked by ~7M real solve attempts).
// `rank` (1 = easiest … 1362 = hardest) and `solveRate` are human-validated difficulty;
// the easy/medium/hard tier is just rank split into thirds for display/selection.

import { solveAll } from './solver'
import { HUMAN_PUZZLES } from './puzzles.data'
import { WURGAFT_CONDITIONS, WURGAFT_PUZZLE_META, WURGAFT_PRACTICE } from './wurgaft.data'
import {
  SUBGOAL_PUZZLES,
  DIVISION_PUZZLES,
  SUBGOAL_CONDITION_ID,
  DIVISION_CONDITION_ID,
  type Phase2Puzzle,
} from './phase2.data'

export type Difficulty = 'easy' | 'medium' | 'hard'

export type Puzzle = {
  id: string
  numbers: number[]
  rank: number // human difficulty rank: 1 = easiest … (pool size) = hardest
  solveRate: number // % of players who solved it
  difficulty: Difficulty // coarse tier derived from rank (thirds)
}

const TOTAL = HUMAN_PUZZLES.length
const tier = (rank: number): Difficulty =>
  rank <= TOTAL / 3 ? 'easy' : rank <= (2 * TOTAL) / 3 ? 'medium' : 'hard'

export const PUZZLES: Puzzle[] = HUMAN_PUZZLES.map(([numbers, rank, solveRate]) => ({
  id: `g24-${String(rank).padStart(4, '0')}`, // id encodes the human rank
  numbers: [...numbers],
  rank,
  solveRate,
  difficulty: tier(rank),
}))

// Pick a random puzzle. Stable for the life of one mount (App memoises the call).
export const pickRandomPuzzle = (): Puzzle => PUZZLES[Math.floor(Math.random() * PUZZLES.length)]

// Pick `count` distinct puzzles in random order for a session (Fisher–Yates shuffle).
export const pickSessionPuzzles = (count: number): Puzzle[] => {
  const shuffled = [...PUZZLES]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled.slice(0, Math.min(count, shuffled.length))
}

// ---- session builders for the two modes ----

const TOTAL_RANKS = 1362
const tierFromRank = (rank: number): Difficulty =>
  rank > 0 && rank <= TOTAL_RANKS / 3 ? 'easy' : rank <= (2 * TOTAL_RANKS) / 3 ? 'medium' : 'hard'
const keyOf = (n: number[]): string => [...n].sort((a, b) => a - b).join(',')
const shuffle = <T>(arr: readonly T[]): T[] => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// Session puzzle = a Puzzle plus its Wurgaft difficulty decile (0 easiest … 9 hardest) and, for
// the Phase-2 contrast arms, the within-arm band it was drawn from. Both are preserved through the
// presentation shuffle so the trace records which decile / band each attempt came from.
export type SessionPuzzle = Puzzle & { decile?: number; band?: string }

// TEST mode (exact Wurgaft replication): pick a random counterbalanced condition, build its
// 10 puzzles (decile = original position), then shuffle presentation order as they did.
export const buildTestSession = (): { conditionId: number; puzzles: SessionPuzzle[] } => {
  const conditionId = Math.floor(Math.random() * WURGAFT_CONDITIONS.length)
  const puzzles = WURGAFT_CONDITIONS[conditionId].map((numbers, decile): SessionPuzzle => {
    const m = WURGAFT_PUZZLE_META[keyOf(numbers)]
    const rank = m?.rank ?? 0
    return {
      id: `w24-c${conditionId}-d${decile}`,
      numbers: [...numbers],
      rank,
      solveRate: m?.solveRate ?? 0,
      difficulty: tierFromRank(rank),
      decile,
    }
  })
  return { conditionId, puzzles: shuffle(puzzles) }
}

// Phase-2 contrast arm: draw `per` puzzles from each of two bands, tag each with its band, then
// shuffle presentation order. Same { conditionId, puzzles } shape as buildTestSession, so the
// caller treats it identically to a full-set test session (10 puzzles, one arm per session).
const buildContrastSession = (
  conditionId: number,
  pool: ReadonlyArray<Phase2Puzzle>,
  bandA: string,
  bandB: string,
  per = 5
): { conditionId: number; puzzles: SessionPuzzle[] } => {
  const draw = (band: string): SessionPuzzle[] =>
    shuffle(pool.filter((p) => p.band === band))
      .slice(0, per)
      .map((p) => ({
        id: p.puzzleId,
        numbers: [...p.numbers],
        rank: p.rank,
        solveRate: p.solveRate,
        difficulty: tierFromRank(p.rank),
        decile: p.decile,
        band: p.band,
      }))
  return { conditionId, puzzles: shuffle([...draw(bandA), ...draw(bandB)]) }
}

// Phase-2 SUBGOAL arm: within-subject 5 high-subgoal + 5 low-subgoal, difficulty-matched.
export const buildSubgoalSession = (): { conditionId: number; puzzles: SessionPuzzle[] } =>
  buildContrastSession(SUBGOAL_CONDITION_ID, SUBGOAL_PUZZLES, 'sg-hi', 'sg-lo')

// Phase-2 DIVISION arm: within-subject 5 division-only + 5 division-free, difficulty-matched.
export const buildDivisionSession = (): { conditionId: number; puzzles: SessionPuzzle[] } =>
  buildContrastSession(DIVISION_CONDITION_ID, DIVISION_PUZZLES, 'dv-only', 'dv-free')

// Warm-up: the 2 Wurgaft practice puzzles, in order.
export const practiceSession = (): SessionPuzzle[] =>
  WURGAFT_PRACTICE.map((numbers, i) => ({
    id: `practice-${i + 1}`,
    numbers: [...numbers],
    rank: 0,
    solveRate: 0,
    difficulty: 'easy',
  }))

// PLAY mode (free play): one random puzzle from the full 1362 pool, optionally filtered to a
// chosen difficulty tier.
export const pickPlayPuzzle = (difficulty?: Difficulty): Puzzle => {
  const pool = difficulty ? PUZZLES.filter((p) => p.difficulty === difficulty) : PUZZLES
  return pool[Math.floor(Math.random() * pool.length)]
}

// Flat list of the 200 Wurgaft problems (each appears once across the 20 conditions), tagged
// with its difficulty decile — the pool the test EXTENSION draws from.
const WURGAFT_ALL: SessionPuzzle[] = (() => {
  const seen = new Set<string>()
  const out: SessionPuzzle[] = []
  for (const cond of WURGAFT_CONDITIONS) {
    cond.forEach((numbers, decile) => {
      const k = keyOf(numbers)
      if (seen.has(k)) return
      seen.add(k)
      const m = WURGAFT_PUZZLE_META[k]
      const rank = m?.rank ?? 0
      out.push({ id: `w24-x-${k.replace(/,/g, '-')}`, numbers: [...numbers], rank, solveRate: m?.solveRate ?? 0, difficulty: tierFromRank(rank), decile })
    })
  }
  return out
})()

// EXTENSION (opt-in extra research puzzles after the core 10): serve `count` Wurgaft problems
// the user has NOT seen before (persistent seen-set passed in), so continuers accumulate more
// coverage of the same 200 across sittings. Falls back to the general pool only in the
// (never-reached in practice) case where all 200 have been exhausted.
export const buildExtensionPuzzles = (count: number, seenKeys: Set<string>): SessionPuzzle[] => {
  const unseen = shuffle(WURGAFT_ALL.filter((p) => !seenKeys.has(keyOf(p.numbers))))
  const picked: SessionPuzzle[] = unseen.slice(0, count).map((p) => ({ ...p, id: `w24-ext-${keyOf(p.numbers).replace(/,/g, '-')}` }))
  while (picked.length < count) picked.push({ ...pickPlayPuzzle(undefined) })
  return picked
}

// Dev-only sanity check. The source set is solvable by construction (and our solver
// already matched its 1362 count), so we only spot-check a random sample to keep dev
// startup fast rather than re-solving all 1362.
export const assertPoolSolvable = (): void => {
  if (process.env.NODE_ENV === 'production') return
  const sample = pickSessionPuzzles(10)
  for (const p of sample) {
    if (solveAll(p.numbers).length === 0) {
      // eslint-disable-next-line no-console
      console.error(`[puzzle pool] ${p.id} (${p.numbers.join(',')}) has no valid solution`)
    }
  }
}
